import { createIconButton } from "../../components/ui/icon-button";
import { createSkipLink } from "../../components/ui/skip-link";
import { attachOrganizerAccountMenuIfAuthenticated } from "../../components/ui/organizer-account-menu";
import { createStandardSiteAppHeader } from "../../components/ui/site-app-header";
import type { Translate } from "../../lib/i18n";
import type { SupportedLocale } from "../../locales/catalog-registry";
import {
  deleteMaterialDocument,
  fetchQuizDetail,
  patchQuizPool,
  patchQuizTitle,
  postCreateDraftQuiz,
  postMaterialFinalize,
  postMaterialUploadUrl,
  postPublishQuizRecipients,
  postQuizGeneration,
  postQuizGenerationPipelineReset,
} from "../../lib/api-quizzes";
import { TRAINING_MATERIAL_FORMATS } from "../../lib/training-material-formats";
import {
  clampGenerationShapeBounds,
  configurationFromSettings,
  MAX_OPTION_COUNT,
  MAX_POOL_GENERATION_COUNT,
  MAX_QUESTIONS_PER_ATTEMPT,
  MIN_OPTION_COUNT,
  MIN_QUESTIONS_PER_ATTEMPT,
  defaultPoolGenerationCount,
  readOptionCount,
  readQuestionsPerAttempt,
  settingsFromConfiguration,
  validateGenerationSettings,
  type GenerationSettingsValues,
} from "./generation-settings";

export interface RenderOrganizerWizardOptions {
  signal: AbortSignal;
  locale: SupportedLocale;
}

const WIZARD_PATH = "/quizzes/wizard";

/**
 * Parses `/quizzes/wizard/{uuid}` from the pathname.
 *
 * @param pathname Normalized (**no** stray trailing slash) history pathname.
 */
export function parseWizardQuizId(pathname: string): string | undefined {
  const base =
    pathname.endsWith("/") && pathname.length > 1
      ? pathname.slice(0, -1)
      : pathname;
  if (base === WIZARD_PATH) {
    return undefined;
  }
  const prefix = `${WIZARD_PATH}/`;
  if (!base.startsWith(prefix)) {
    return undefined;
  }
  const rest = base.slice(prefix.length);
  if (
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      rest,
    )
  ) {
    return undefined;
  }
  return rest;
}

interface QuizTrainingDocumentRow {
  training_document_id: string;
  ordinal: number;
  original_filename: string;
  content_type: string;
  upload_state: string;
  source_object_key: string;
  extract_object_key: string | null;
  last_error_code: string | null;
  last_error_detail: string | null;
}

export interface QuizChoiceObject {
  text: string;
  correct: boolean;
}

export interface QuizQuestionRow {
  question_id: string;
  stem: string;
  choices: QuizChoiceObject[];
  active: boolean;
  provenance?: string | null;
}

export function correctIndicesForQuizRow(q: QuizQuestionRow): number[] {
  return q.choices
    .map((c, i) => (c.correct ? i : -1))
    .filter((i) => i >= 0)
    .sort((a, b) => a - b);
}

function quizChoiceTextAt(q: QuizQuestionRow, index: number): string {
  return q.choices[index]?.text ?? "";
}

export interface QuizDetailPayload {
  quiz_id: string;
  title: string;
  status: string;
  created_at: string;
  invitation_count: number;
  training_documents?: QuizTrainingDocumentRow[];
  material_all_ready?: boolean;
  generation_pipeline_status?: string;
  configuration?: Record<string, unknown>;
  questions?: QuizQuestionRow[];
}

export function isQuizDetail(data: unknown): data is QuizDetailPayload {
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const rec = data as Record<string, unknown>;
  if (
    typeof rec.quiz_id !== "string" ||
    typeof rec.title !== "string" ||
    typeof rec.status !== "string" ||
    typeof rec.created_at !== "string" ||
    typeof rec.invitation_count !== "number"
  ) {
    return false;
  }
  const td = rec.training_documents;
  if (td !== undefined) {
    if (!Array.isArray(td)) {
      return false;
    }
    for (const row of td) {
      if (typeof row !== "object" || row === null) {
        return false;
      }
      const r = row as Record<string, unknown>;
      if (
        typeof r.training_document_id !== "string" ||
        typeof r.ordinal !== "number" ||
        typeof r.original_filename !== "string" ||
        typeof r.content_type !== "string" ||
        typeof r.upload_state !== "string"
      ) {
        return false;
      }
    }
  }
  const mar = rec.material_all_ready;
  if (mar !== undefined && typeof mar !== "boolean") {
    return false;
  }
  const gen = rec.generation_pipeline_status;
  if (gen !== undefined && typeof gen !== "string") {
    return false;
  }
  const cfg = rec.configuration;
  if (cfg !== undefined) {
    if (typeof cfg !== "object" || cfg === null || Array.isArray(cfg)) {
      return false;
    }
  }
  const qs = rec.questions;
  if (qs !== undefined) {
    if (!Array.isArray(qs)) {
      return false;
    }
    for (const row of qs) {
      if (typeof row !== "object" || row === null) {
        return false;
      }
      const r = row as Record<string, unknown>;
      if (
        typeof r.question_id !== "string" ||
        typeof r.stem !== "string" ||
        typeof r.active !== "boolean"
      ) {
        return false;
      }
      const ch = r.choices;
      if (!Array.isArray(ch) || ch.length === 0) {
        return false;
      }
      for (const opt of ch) {
        if (typeof opt !== "object" || opt === null) {
          return false;
        }
        const co = opt as Record<string, unknown>;
        if (typeof co.text !== "string" || typeof co.correct !== "boolean") {
          return false;
        }
      }
    }
  }
  return true;
}

interface PublishInvitationRow {
  email: string;
  mail: string;
  detail?: string;
}

function isPublishPayload(
  data: unknown,
): data is { invitations: PublishInvitationRow[] } {
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const outer = data as Record<string, unknown>;
  const inv = outer.invitations;
  if (!Array.isArray(inv)) {
    return false;
  }
  for (const row of inv) {
    if (typeof row !== "object" || row === null) {
      return false;
    }
    const ir = row as Record<string, unknown>;
    if (typeof ir.email !== "string" || typeof ir.mail !== "string") {
      return false;
    }
    if ("detail" in ir && typeof ir.detail !== "string") {
      return false;
    }
  }
  return true;
}

function formatWizardDetailDate(iso: string, locale: SupportedLocale): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) {
    return iso;
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(ms));
}

function appendLabeledNumberInput(
  parent: HTMLElement,
  labelText: string,
  attrs: {
    id: string;
    value: number;
    min: number;
    max: number;
    disabled?: boolean;
  },
): HTMLInputElement {
  const wrap = document.createElement("label");
  wrap.className = "quiz-wizard__field";
  const span = document.createElement("span");
  span.className = "quiz-wizard__full-label";
  span.textContent = labelText;
  wrap.appendChild(span);
  const input = document.createElement("input");
  input.type = "number";
  input.id = attrs.id;
  input.className = "quiz-wizard__number-input";
  input.value = String(attrs.value);
  input.min = String(attrs.min);
  input.max = String(attrs.max);
  if (attrs.disabled) {
    input.disabled = true;
  }
  wrap.appendChild(input);
  parent.appendChild(wrap);
  return input;
}

function readGenerationSettingsFromInputs(
  qpaInput: HTMLInputElement,
  poolInput: HTMLInputElement,
  optionInput: HTMLInputElement,
  minCorrectInput: HTMLInputElement,
  maxCorrectInput: HTMLInputElement,
  initial: GenerationSettingsValues,
): GenerationSettingsValues {
  const questionsPerAttempt = Math.max(
    MIN_QUESTIONS_PER_ATTEMPT,
    Math.min(
      MAX_QUESTIONS_PER_ATTEMPT,
      parseInt(qpaInput.value, 10) || initial.questionsPerAttempt,
    ),
  );
  const poolGenerationCount = Math.max(
    1,
    Math.min(
      MAX_POOL_GENERATION_COUNT,
      parseInt(poolInput.value, 10) || initial.poolGenerationCount,
    ),
  );
  const optionCount = Math.max(
    MIN_OPTION_COUNT,
    Math.min(
      MAX_OPTION_COUNT,
      parseInt(optionInput.value, 10) || initial.optionCount,
    ),
  );
  const minRaw = parseInt(minCorrectInput.value, 10);
  const maxRaw = parseInt(maxCorrectInput.value, 10);
  const minCorrect = Number.isFinite(minRaw)
    ? minRaw
    : initial.minCorrectOptions;
  const maxCorrect = Number.isFinite(maxRaw)
    ? maxRaw
    : initial.maxCorrectOptions;
  const { minCorrectOptions, maxCorrectOptions } = clampGenerationShapeBounds(
    optionCount,
    minCorrect,
    maxCorrect,
  );
  return {
    questionsPerAttempt,
    poolGenerationCount,
    optionCount,
    minCorrectOptions,
    maxCorrectOptions,
  };
}

/** Keeps DOM attributes and values within 1 <= min <= max <= optionCount. */
function syncGenerationShapeInputs(
  optionCount: number,
  minCorrectInput: HTMLInputElement,
  maxCorrectInput: HTMLInputElement,
): void {
  const oc = Math.max(
    MIN_OPTION_COUNT,
    Math.min(MAX_OPTION_COUNT, Math.floor(optionCount)),
  );
  minCorrectInput.min = "1";
  minCorrectInput.max = String(oc);
  maxCorrectInput.min = "1";
  maxCorrectInput.max = String(oc);
  const minRaw = parseInt(minCorrectInput.value, 10);
  const maxRaw = parseInt(maxCorrectInput.value, 10);
  const minFallback = Number.isFinite(minRaw) ? minRaw : 1;
  const maxFallback = Number.isFinite(maxRaw) ? maxRaw : 1;
  const { minCorrectOptions, maxCorrectOptions } = clampGenerationShapeBounds(
    oc,
    minFallback,
    maxFallback,
  );
  minCorrectInput.value = String(minCorrectOptions);
  maxCorrectInput.value = String(maxCorrectOptions);
}

function mailOutcomeLabel(t: Translate, outcome: string): string {
  if (outcome === "sent") {
    return t("quiz.wizard.mail_sent");
  }
  if (outcome === "failed") {
    return t("quiz.wizard.mail_failed");
  }
  if (outcome === "skipped") {
    return t("quiz.wizard.mail_skipped");
  }
  return outcome;
}

/**
 * Whether **step** **1** **Continue** may advance: at least one document, every row
 * **READY**, server **material_all_ready**, and no direct upload in flight.
 *
 * @param detail - Latest quiz detail payload.
 * @param uploadInFlight - True while presigned PUT or finalize runs for material.
 */
export function materialStepCanContinue(
  detail: QuizDetailPayload,
  uploadInFlight: boolean,
): boolean {
  if (uploadInFlight) {
    return false;
  }
  const docs = detail.training_documents ?? [];
  if (docs.length === 0) {
    return false;
  }
  if (detail.material_all_ready !== true) {
    return false;
  }
  return docs.every((d) => d.upload_state === "READY");
}

function materialUploadStateLabel(t: Translate, uploadState: string): string {
  switch (uploadState) {
    case "AWAITING_BYTES":
      return t("quiz.wizard.material_state_AWAITING_BYTES");
    case "INGESTING":
      return t("quiz.wizard.material_state_INGESTING");
    case "READY":
      return t("quiz.wizard.material_state_READY");
    case "FAILED":
      return t("quiz.wizard.material_state_FAILED");
    default:
      return uploadState;
  }
}

function materialDeleteIconButton(
  t: Translate,
  onClick: () => void,
): HTMLButtonElement {
  const removeBtn = document.createElement("button");
  removeBtn.type = "button";
  removeBtn.className = "quiz-wizard__material-delete";
  removeBtn.setAttribute("aria-label", t("quiz.wizard.material_delete_aria"));
  const svgNs = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(svgNs, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "14");
  svg.setAttribute("height", "14");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(svgNs, "path");
  path.setAttribute("d", "M6 6 L18 18 M18 6 L6 18");
  path.setAttribute("stroke", "#ffffff");
  path.setAttribute("stroke-width", "2.5");
  path.setAttribute("stroke-linecap", "round");
  svg.appendChild(path);
  removeBtn.appendChild(svg);
  removeBtn.addEventListener("click", onClick);
  return removeBtn;
}

const STEP_TOTAL = 5;

/** Highest **wizard** step number stored in **`?step=`** (the final screen is publish success, not a step). */
const WIZARD_STEP_QUERY_MAX = STEP_TOTAL - 1;

const WIZARD_STEP_QUERY = "step";

/**
 * Reads **`step`** from a **`location.search`** string (**1**..**`WIZARD_STEP_QUERY_MAX`**), default **1**.
 *
 * @param search - Raw query string including **`?`** or **`""`**.
 */
export function readOrganizerWizardStepFromSearch(search: string): number {
  const raw = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  ).get(WIZARD_STEP_QUERY);
  if (raw === null) {
    return 1;
  }
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 1 || n > WIZARD_STEP_QUERY_MAX) {
    return 1;
  }
  return n;
}

/**
 * Path (and optional **`?step=`**) for the organizer wizard so refresh restores the same step.
 *
 * @param quizId - Draft quiz id from the path.
 * @param step - Requested step (clamped to **1**..**`WIZARD_STEP_QUERY_MAX`**).
 */
export function organizerWizardPathForStep(
  quizId: string,
  step: number,
): string {
  const base = `${WIZARD_PATH}/${quizId}`;
  const clamped = Math.max(1, Math.min(step, WIZARD_STEP_QUERY_MAX));
  if (clamped <= 1) {
    return base;
  }
  return `${base}?${WIZARD_STEP_QUERY}=${String(clamped)}`;
}

/**
 * Renders organizer training quiz setup wizard (**wizard** aligns with **`DD-SRS-F-TRAINING-QUIZ`.14**
 * sequencing; **`POST`** **publish** is **MVP** bridge until material and generation endpoints ship).
 *
 * @param mount - Container cleared before render.
 * @param t - Localized resolver.
 * @param options - Abort signal (**fetch** honours it) and active locale for dates.
 */
export async function renderOrganizerWizard(
  mount: HTMLElement,
  t: Translate,
  options: RenderOrganizerWizardOptions,
): Promise<void> {
  mount.replaceChildren();
  mount.className = "quiz-wizard";
  mount.appendChild(
    createSkipLink(t("quiz.home.skip_to_main"), "quiz-wizard-main"),
  );

  const { header: appHeader, authSlot } = createStandardSiteAppHeader(
    t,
    options.locale,
  );
  appHeader.classList.add("home-header");
  mount.appendChild(appHeader);

  void attachOrganizerAccountMenuIfAuthenticated(authSlot, t, {
    signal: options.signal,
  });

  let quizIdFromUrl = parseWizardQuizId(window.location.pathname);
  let currentStep =
    quizIdFromUrl !== undefined
      ? readOrganizerWizardStepFromSearch(window.location.search)
      : 1;
  let lastPublishRows: PublishInvitationRow[] | null = null;

  let detailCache: QuizDetailPayload | null = null;
  /** Clears **`GET`** **poll** (**step** **1** **material** **ingest**) before every **paint**. */
  let materialStepPollTimer: number | null = null;
  /** True while a **training** material file is uploading (**presign**, **PUT**, **finalize**). */
  let materialStepUploadInFlight = false;
  /** Continue button on **step** **1** (**null** when not rendered). Used to toggle **disabled** without repainting mid-upload. */
  let materialStepContinueBtn: HTMLButtonElement | null = null;

  const clearMaterialStepPoll = (): void => {
    if (materialStepPollTimer !== null) {
      window.clearInterval(materialStepPollTimer);
      materialStepPollTimer = null;
    }
  };
  /** Clears **`GET`** **poll** (**step** **3** **running**) before every **paint**. */
  let step3PollTimer: number | null = null;
  const clearStep3Poll = (): void => {
    if (step3PollTimer !== null) {
      window.clearInterval(step3PollTimer);
      step3PollTimer = null;
    }
  };

  const header = document.createElement("header");
  header.className = "quiz-wizard__header";
  const h1 = document.createElement("h1");
  h1.className = "quiz-wizard__title";
  h1.textContent = t("quiz.wizard.page_title");
  header.appendChild(h1);
  mount.appendChild(header);

  const nav = document.createElement("nav");
  nav.className = "quiz-wizard__nav";

  const backList = document.createElement("a");
  backList.href = "/quizzes";
  backList.textContent = t("quiz.wizard.back_my_quizzes");
  nav.appendChild(backList);

  mount.appendChild(nav);

  const status = document.createElement("p");
  status.className = "quiz-wizard__status";
  status.setAttribute("aria-live", "polite");

  const panel = document.createElement("section");
  panel.className = "quiz-wizard__panel";

  const mainEl = document.createElement("main");
  mainEl.id = "quiz-wizard-main";
  mainEl.className = "quiz-wizard__main";
  mainEl.tabIndex = -1;
  mainEl.appendChild(status);
  mainEl.appendChild(panel);
  mount.appendChild(mainEl);

  /** Puts **status** above **panel** (steps **1**, **2**, **4**). Step **3** re-parents it beside the generate button. */
  const dockStatusBeforePanel = (): void => {
    if (status.parentElement !== mainEl || status.nextSibling !== panel) {
      status.remove();
      mainEl.insertBefore(status, panel);
    }
  };

  const footerNote = document.createElement("p");
  footerNote.className = "quiz-wizard__footer-note";
  footerNote.textContent = t("quiz.wizard.preview_note");
  mount.appendChild(footerNote);

  const setProgress = (stepNum: number): void => {
    header.querySelector(".quiz-wizard__progress")?.remove();
    const prog = document.createElement("p");
    prog.className = "quiz-wizard__progress";
    prog.textContent = t("quiz.wizard.progress", {
      step: stepNum,
      total: STEP_TOTAL,
    });
    header.appendChild(prog);
  };

  /**
   * Refreshes **quiz** **detail** (**GET** **`/api/v1/quizzes/:id`**); returns **false** when the view closes.
   */
  const reloadDetail = async (qid: string): Promise<boolean> => {
    const res = await fetchQuizDetail(qid, { signal: options.signal });
    if (options.signal.aborted) {
      return false;
    }
    if (!res.ok) {
      detailCache = null;
      if (res.status === 401) {
        status.textContent = t("quiz.wizard.need_sign_in");
      } else if (res.status === 404) {
        status.textContent = t("quiz.my_quizzes.feature_off");
      } else {
        status.textContent = t("quiz.wizard.quiz_missing");
      }
      return false;
    }
    let data: unknown;
    try {
      data = (await res.json()) as unknown;
    } catch {
      status.textContent = t("quiz.wizard.quiz_missing");
      detailCache = null;
      return false;
    }
    if (!isQuizDetail(data)) {
      status.textContent = t("quiz.wizard.quiz_missing");
      detailCache = null;
      return false;
    }
    detailCache = data;
    return true;
  };

  /** Syncs Continue disabled state when detail or upload state changes mid-render. */
  const syncMaterialStepContinueDisabled = (): void => {
    if (
      materialStepContinueBtn === null ||
      !materialStepContinueBtn.isConnected ||
      !detailCache
    ) {
      return;
    }
    materialStepContinueBtn.disabled = !materialStepCanContinue(
      detailCache,
      materialStepUploadInFlight,
    );
  };

  /** Keeps **`history`** in sync so reload restores **`?step=`** when a draft quiz is loaded. */
  const syncWizardUrl = (): void => {
    if (!quizIdFromUrl) {
      return;
    }
    const next = organizerWizardPathForStep(quizIdFromUrl, currentStep);
    if (`${window.location.pathname}${window.location.search}` !== next) {
      window.history.replaceState(null, "", next);
    }
  };

  /** Drops **`?step=`** once the flow reaches the publish-success screen. */
  const stripWizardStepQueryFromUrl = (): void => {
    if (!quizIdFromUrl || window.location.search === "") {
      return;
    }
    window.history.replaceState(null, "", `${WIZARD_PATH}/${quizIdFromUrl}`);
  };

  /** Re-render the active step (**closure** captures latest **quiz** identity). */
  const paintStep = (): void => {
    clearMaterialStepPoll();
    clearStep3Poll();
    if (!quizIdFromUrl) {
      setProgress(1);
      panel.replaceChildren();
      const intro = document.createElement("p");
      intro.className = "quiz-wizard__intro";
      intro.textContent = t("quiz.wizard.intro_lead");
      panel.appendChild(intro);

      const createBtn = document.createElement("button");
      createBtn.type = "button";
      createBtn.className = "quiz-wizard__primary";
      createBtn.textContent = t("quiz.wizard.create_draft_button");
      panel.appendChild(createBtn);

      createBtn.addEventListener("click", () => {
        void (async () => {
          createBtn.disabled = true;
          status.textContent = "";
          try {
            const res = await postCreateDraftQuiz({ signal: options.signal });
            if (options.signal.aborted) {
              return;
            }
            if (res.status === 404) {
              status.textContent = t("quiz.my_quizzes.feature_off");
              createBtn.disabled = false;
              return;
            }
            if (!res.ok) {
              status.textContent = t("quiz.wizard.create_failed");
              createBtn.disabled = false;
              return;
            }
            let body: unknown;
            try {
              body = (await res.json()) as unknown;
            } catch {
              status.textContent = t("quiz.wizard.create_failed");
              createBtn.disabled = false;
              return;
            }
            const rec =
              typeof body === "object" &&
              body !== null &&
              typeof (body as { quiz_id?: unknown }).quiz_id === "string"
                ? (body as { quiz_id: string }).quiz_id
                : null;
            if (!rec) {
              status.textContent = t("quiz.wizard.create_failed");
              createBtn.disabled = false;
              return;
            }
            quizIdFromUrl = rec;
            window.history.replaceState(
              null,
              "",
              `/quizzes/wizard/${quizIdFromUrl}`,
            );
            currentStep = 1;
            if (!(await reloadDetail(quizIdFromUrl))) {
              createBtn.disabled = false;
              return;
            }
            paintStep();
          } catch (err: unknown) {
            if (err instanceof DOMException && err.name === "AbortError") {
              return;
            }
            status.textContent = t("quiz.wizard.create_failed");
          } finally {
            createBtn.disabled = false;
          }
        })();
      });
      return;
    }

    /** From here onward **quizIdFromUrl** is defined (**assert** for **closure** narrowing). */
    const qid = quizIdFromUrl as string;

    if (!detailCache || detailCache.quiz_id !== qid) {
      void reloadDetail(qid).then((ok: boolean) => {
        if (ok) {
          paintStep();
        }
      });
      dockStatusBeforePanel();
      panel.replaceChildren();
      status.textContent = t("quiz.wizard.loading_quiz");
      setProgress(Math.min(currentStep, STEP_TOTAL));
      syncWizardUrl();
      return;
    }

    if (
      detailCache.status === "published" &&
      lastPublishRows === null
    ) {
      window.location.replace(`/quizzes/view/${encodeURIComponent(qid)}`);
      return;
    }

    status.textContent = "";

    const publishedDone =
      detailCache.status === "published" || lastPublishRows !== null;

    if (publishedDone) {
      stripWizardStepQueryFromUrl();
      setProgress(STEP_TOTAL);
      panel.replaceChildren();

      const doneTitle = document.createElement("h2");
      doneTitle.textContent = t("quiz.wizard.success_title");
      panel.appendChild(doneTitle);

      const doneLead = document.createElement("p");
      doneLead.textContent = lastPublishRows
        ? t("quiz.wizard.success_lead_publish")
        : t("quiz.wizard.success_lead_repeat", {
            count: detailCache.invitation_count,
          });
      panel.appendChild(doneLead);

      const sesNote = document.createElement("p");
      sesNote.className = "quiz-wizard__ses-note";
      sesNote.textContent = t("quiz.wizard.mail_notice_ses_optional");
      panel.appendChild(sesNote);

      if (
        lastPublishRows &&
        lastPublishRows.length > 0 &&
        lastPublishRows.every((r) => r.mail === "skipped")
      ) {
        const skipLine = document.createElement("p");
        skipLine.className = "quiz-wizard__highlight";
        skipLine.textContent = t("quiz.wizard.mail_notice_skipped");
        panel.appendChild(skipLine);
      }

      if (lastPublishRows && lastPublishRows.length > 0) {
        const tableWrap = document.createElement("div");
        tableWrap.className = "quiz-wizard__table-wrap";
        const tbl = document.createElement("table");
        tbl.className = "quiz-wizard__delivery-table";

        const thead = document.createElement("thead");
        const hr = document.createElement("tr");
        const hEmail = document.createElement("th");
        hEmail.scope = "col";
        hEmail.textContent = t("quiz.wizard.col_email");
        const hMail = document.createElement("th");
        hMail.scope = "col";
        hMail.textContent = t("quiz.wizard.col_delivery");
        hr.appendChild(hEmail);
        hr.appendChild(hMail);
        thead.appendChild(hr);
        tbl.appendChild(thead);

        const tbody = document.createElement("tbody");
        for (const row of lastPublishRows) {
          const tr = document.createElement("tr");
          const tdEmail = document.createElement("td");
          tdEmail.textContent = row.email;
          const tdMail = document.createElement("td");
          tdMail.textContent = mailOutcomeLabel(t, row.mail);
          tr.appendChild(tdEmail);
          tr.appendChild(tdMail);
          tbody.appendChild(tr);
        }
        tbl.appendChild(tbody);
        tableWrap.appendChild(tbl);
        panel.appendChild(tableWrap);
      }

      const backDash = document.createElement("p");
      const dashLink = document.createElement("a");
      dashLink.href = "/quizzes";
      dashLink.textContent = t("quiz.wizard.back_to_my_quizzes");
      backDash.appendChild(dashLink);
      panel.appendChild(backDash);

      const detailNav = document.createElement("p");
      const detailLink = document.createElement("a");
      detailLink.href = `/quizzes/view/${encodeURIComponent(qid)}`;
      detailLink.textContent = t("quiz.wizard.view_quiz_detail");
      detailNav.appendChild(detailLink);
      panel.appendChild(detailNav);
      return;
    }

    const stepClamp = Math.max(1, Math.min(currentStep, STEP_TOTAL - 1));
    currentStep = stepClamp;
    syncWizardUrl();

    switch (stepClamp) {
      case 1:
        dockStatusBeforePanel();
        setProgress(1);
        panel.replaceChildren();
        materialStepContinueBtn = null;
        {
          const s1Heading = document.createElement("h2");
          s1Heading.textContent = t("quiz.wizard.step_training_heading");
          panel.appendChild(s1Heading);
          const s1Lead = document.createElement("p");
          s1Lead.className = "quiz-wizard__step-lead";
          s1Lead.textContent = t("quiz.wizard.step_training_body");
          panel.appendChild(s1Lead);

          const docsWrap = document.createElement("div");
          docsWrap.className = "quiz-wizard__material-docs";
          const docsTitle = document.createElement("h3");
          docsTitle.className = "quiz-wizard__subhead";
          docsTitle.textContent = t("quiz.wizard.material_docs_heading");
          docsWrap.appendChild(docsTitle);
          const docList = document.createElement("ul");
          docList.className = "quiz-wizard__material-list";
          const tdocs = detailCache.training_documents ?? [];
          if (tdocs.length === 0) {
            const empty = document.createElement("li");
            empty.className = "quiz-wizard__material-empty";
            empty.textContent = t("quiz.wizard.material_empty");
            docList.appendChild(empty);
          } else {
            for (const row of tdocs) {
              const li = document.createElement("li");
              li.className = "quiz-wizard__material-item";
              const name = document.createElement("span");
              name.className = "quiz-wizard__material-name";
              name.textContent = row.original_filename;
              const st = document.createElement("span");
              st.className = "quiz-wizard__material-state";
              st.textContent = materialUploadStateLabel(t, row.upload_state);
              li.appendChild(name);
              li.appendChild(st);
              const removeBtn = materialDeleteIconButton(t, () => {
                void (async () => {
                  removeBtn.disabled = true;
                  try {
                    const res = await deleteMaterialDocument(
                      qid,
                      row.training_document_id,
                      { signal: options.signal },
                    );
                    if (options.signal.aborted) {
                      return;
                    }
                    if (!res.ok) {
                      status.textContent = t(
                        "quiz.wizard.material_remove_failed",
                      );
                      removeBtn.disabled = false;
                      return;
                    }
                    status.textContent = "";
                    if (!(await reloadDetail(qid))) {
                      removeBtn.disabled = false;
                      return;
                    }
                    paintStep();
                  } catch (err: unknown) {
                    if (
                      err instanceof DOMException &&
                      err.name === "AbortError"
                    ) {
                      return;
                    }
                    status.textContent = t(
                      "quiz.wizard.material_remove_failed",
                    );
                    removeBtn.disabled = false;
                  }
                })();
              });
              li.appendChild(removeBtn);
              if (row.upload_state === "FAILED" && row.last_error_detail) {
                const err = document.createElement("span");
                err.className = "quiz-wizard__material-err";
                err.textContent = row.last_error_detail;
                li.appendChild(err);
              }
              docList.appendChild(li);
            }
          }
          docsWrap.appendChild(docList);
          panel.appendChild(docsWrap);

          const dropZone = document.createElement("div");
          dropZone.className = "quiz-wizard__material-dropzone";
          dropZone.setAttribute("role", "group");
          dropZone.setAttribute(
            "aria-label",
            t("quiz.wizard.material_drop_aria"),
          );

          const hiddenLabel = document.createElement("label");
          hiddenLabel.className = "quiz-wizard__visually-hidden";
          hiddenLabel.htmlFor = "quiz-wizard-material-file";
          hiddenLabel.textContent = t("quiz.wizard.material_upload_pick");

          const fileInp = document.createElement("input");
          fileInp.type = "file";
          fileInp.id = "quiz-wizard-material-file";
          fileInp.accept = TRAINING_MATERIAL_FORMATS.filePickerAccept;
          fileInp.className =
            "quiz-wizard__file-input quiz-wizard__file-input--sr";

          const hint = document.createElement("p");
          hint.className = "quiz-wizard__material-drop-hint";
          hint.textContent = t("quiz.wizard.material_drop_hint");

          const chooseBtn = document.createElement("button");
          chooseBtn.type = "button";
          chooseBtn.className = "quiz-wizard__material-choose";
          chooseBtn.textContent = t("quiz.wizard.material_choose_file");

          const clearDropHighlight = (): void => {
            dropZone.classList.remove("quiz-wizard__material-dropzone--active");
          };
          const setMaterialChromeBusy = (busy: boolean): void => {
            chooseBtn.disabled = busy;
            fileInp.disabled = busy;
            dropZone.classList.toggle(
              "quiz-wizard__material-dropzone--busy",
              busy,
            );
            dropZone.setAttribute("aria-busy", busy ? "true" : "false");
          };

          const uploadTrainingMaterialFile = async (f: File): Promise<void> => {
            if (materialStepUploadInFlight) {
              return;
            }
            materialStepUploadInFlight = true;
            syncMaterialStepContinueDisabled();
            clearDropHighlight();
            setMaterialChromeBusy(true);
            status.textContent = t("quiz.wizard.material_busy");
            try {
              const pre = await postMaterialUploadUrl(
                qid,
                {
                  contentType: f.type || "application/octet-stream",
                  filename: f.name,
                  byteSize: f.size,
                },
                { signal: options.signal },
              );
              if (options.signal.aborted) {
                return;
              }
              if (pre.status === 503) {
                status.textContent = t("quiz.wizard.material_storage_off");
                return;
              }
              if (pre.status === 409) {
                status.textContent = t("quiz.wizard.material_limit");
                return;
              }
              if (!pre.ok) {
                status.textContent = t("quiz.wizard.material_failed");
                return;
              }
              let preBody: unknown;
              try {
                preBody = (await pre.json()) as unknown;
              } catch {
                status.textContent = t("quiz.wizard.material_failed");
                return;
              }
              if (
                typeof preBody !== "object" ||
                preBody === null ||
                typeof (preBody as { url?: unknown }).url !== "string" ||
                typeof (preBody as { objectKey?: unknown }).objectKey !==
                  "string" ||
                typeof (preBody as { trainingDocumentId?: unknown })
                  .trainingDocumentId !== "string"
              ) {
                status.textContent = t("quiz.wizard.material_failed");
                return;
              }
              const pb = preBody as {
                url: string;
                objectKey: string;
                trainingDocumentId: string;
                headers?: Record<string, string>;
              };
              const putHeaders = new Headers();
              const ct =
                (pb.headers && pb.headers["Content-Type"]) ||
                f.type ||
                "application/octet-stream";
              putHeaders.set("Content-Type", ct);
              const putRes = await fetch(pb.url, {
                method: "PUT",
                body: f,
                headers: putHeaders,
                signal: options.signal,
              });
              if (options.signal.aborted) {
                return;
              }
              if (!putRes.ok) {
                status.textContent = t("quiz.wizard.material_put_failed");
                return;
              }
              const fin = await postMaterialFinalize(
                qid,
                {
                  trainingDocumentId: pb.trainingDocumentId,
                  objectKey: pb.objectKey,
                  contentType: ct,
                  byteSize: f.size,
                },
                { signal: options.signal },
              );
              if (options.signal.aborted) {
                return;
              }
              if (fin.status === 400 || fin.status === 409) {
                status.textContent = t("quiz.wizard.material_failed");
                void reloadDetail(qid);
                paintStep();
                return;
              }
              if (!fin.ok) {
                status.textContent = t("quiz.wizard.material_failed");
                return;
              }
              status.textContent = "";
              if (!(await reloadDetail(qid))) {
                return;
              }
              paintStep();
            } catch (err: unknown) {
              if (err instanceof DOMException && err.name === "AbortError") {
                return;
              }
              status.textContent = t("quiz.wizard.material_failed");
            } finally {
              materialStepUploadInFlight = false;
              syncMaterialStepContinueDisabled();
              setMaterialChromeBusy(false);
              fileInp.value = "";
            }
          };

          dropZone.addEventListener("dragenter", (e) => {
            e.preventDefault();
            dropZone.classList.add("quiz-wizard__material-dropzone--active");
          });
          dropZone.addEventListener("dragleave", (e) => {
            e.preventDefault();
            const rt = e.relatedTarget;
            if (rt instanceof Node && dropZone.contains(rt)) {
              return;
            }
            clearDropHighlight();
          });
          dropZone.addEventListener("dragover", (e) => {
            e.preventDefault();
          });
          dropZone.addEventListener("drop", (e) => {
            e.preventDefault();
            clearDropHighlight();
            const dropped = e.dataTransfer?.files?.[0];
            if (dropped) {
              void uploadTrainingMaterialFile(dropped);
            }
          });

          chooseBtn.addEventListener("click", () => {
            fileInp.click();
          });

          fileInp.addEventListener("change", () => {
            const f = fileInp.files?.[0];
            if (!f) {
              status.textContent = t("quiz.wizard.material_no_file");
              return;
            }
            void uploadTrainingMaterialFile(f);
          });

          dropZone.appendChild(hiddenLabel);
          dropZone.appendChild(fileInp);
          dropZone.appendChild(hint);
          dropZone.appendChild(chooseBtn);
          panel.appendChild(dropZone);

          const strip = document.createElement("div");
          strip.className = "quiz-wizard__buttons";

          const next = document.createElement("button");
          next.type = "button";
          next.className = "quiz-wizard__primary";
          next.textContent = t("quiz.wizard.continue");
          materialStepContinueBtn = next;
          next.disabled = !materialStepCanContinue(
            detailCache,
            materialStepUploadInFlight,
          );
          next.addEventListener("click", () => {
            if (
              !detailCache ||
              !materialStepCanContinue(detailCache, materialStepUploadInFlight)
            ) {
              status.textContent = t("quiz.wizard.material_next_blocked");
              return;
            }
            currentStep = 2;
            paintStep();
          });
          strip.appendChild(next);
          panel.appendChild(strip);

          if (
            (detailCache.training_documents?.length ?? 0) > 0 &&
            detailCache.material_all_ready !== true
          ) {
            materialStepPollTimer = window.setInterval(() => {
              void (async () => {
                if (!(await reloadDetail(qid))) {
                  return;
                }
                paintStep();
              })();
            }, 2100);
          }
        }
        return;

      case 2:
        dockStatusBeforePanel();
        setProgress(2);
        panel.replaceChildren();
        const s2Heading = document.createElement("h2");
        s2Heading.textContent = t("quiz.wizard.step_title_name");
        panel.appendChild(s2Heading);

        const s2Lead = document.createElement("p");
        s2Lead.textContent = t("quiz.wizard.step_title_help");
        panel.appendChild(s2Lead);

        const createdP = document.createElement("p");
        createdP.className = "quiz-wizard__step-created";
        const createdTime = document.createElement("time");
        createdTime.dateTime = detailCache.created_at;
        createdTime.textContent = t("quiz.detail.created_line", {
          date: formatWizardDetailDate(detailCache.created_at, options.locale),
        });
        createdP.appendChild(createdTime);
        panel.appendChild(createdP);

        const fieldWrap = document.createElement("div");
        fieldWrap.className = "quiz-wizard__field";
        const label = document.createElement("label");
        label.className = "quiz-wizard__full-label";
        label.htmlFor = "quiz-wizard-title-input";
        label.textContent = t("quiz.my_quizzes.quiz_title_field");

        const input = document.createElement("input");
        input.id = "quiz-wizard-title-input";
        input.type = "text";
        input.className = "quiz-wizard__text-input";
        input.maxLength = 512;
        input.value = detailCache.title;
        input.placeholder = t("quiz.my_quizzes.untitled_placeholder");

        fieldWrap.appendChild(label);
        fieldWrap.appendChild(input);

        panel.appendChild(fieldWrap);

        const titleStrip = document.createElement("div");
        titleStrip.className = "quiz-wizard__buttons";

        const titleBack = document.createElement("button");
        titleBack.type = "button";
        titleBack.className = "quiz-wizard__secondary";
        titleBack.textContent = t("quiz.wizard.back_step");
        titleBack.addEventListener("click", () => {
          currentStep = 1;
          paintStep();
        });
        titleStrip.appendChild(titleBack);

        const next1 = document.createElement("button");
        next1.type = "button";
        next1.className = "quiz-wizard__primary";
        next1.textContent = t("quiz.wizard.save_title_continue");

        titleStrip.appendChild(next1);
        panel.appendChild(titleStrip);

        next1.addEventListener("click", () => {
          void (async () => {
            next1.disabled = true;
            try {
              const res = await patchQuizTitle(qid, input.value.trim(), {
                signal: options.signal,
              });
              if (options.signal.aborted) {
                return;
              }
              if (!res.ok) {
                status.textContent = t("quiz.my_quizzes.title_save_failed");
                next1.disabled = false;
                return;
              }
              if (!(await reloadDetail(qid))) {
                next1.disabled = false;
                return;
              }
              currentStep = 3;
              paintStep();
            } catch (err: unknown) {
              if (err instanceof DOMException && err.name === "AbortError") {
                return;
              }
              status.textContent = t("quiz.my_quizzes.title_save_failed");
            } finally {
              next1.disabled = false;
            }
          })();
        });
        return;

      case 3:
        setProgress(3);
        panel.replaceChildren();
        const s3Heading = document.createElement("h2");
        s3Heading.textContent = t("quiz.wizard.step_generation_heading");
        panel.appendChild(s3Heading);
        const s3Lead = document.createElement("p");
        s3Lead.textContent = t("quiz.wizard.step_generation_body");
        panel.appendChild(s3Lead);

        const pipe = detailCache.generation_pipeline_status ?? "idle";

        const genSettingsInitial = settingsFromConfiguration(
          detailCache.configuration,
        );
        let poolCountTouched =
          detailCache.configuration?.pool_generation_count !== undefined;

        const settingsBlock = document.createElement("fieldset");
        settingsBlock.className = "quiz-wizard__generation-settings";
        settingsBlock.disabled = pipe === "running";

        const settingsHeading = document.createElement("legend");
        settingsHeading.className = "quiz-wizard__subhead";
        settingsHeading.textContent = t(
          "quiz.wizard.generation_settings_heading",
        );
        settingsBlock.appendChild(settingsHeading);

        const settingsHint = document.createElement("p");
        settingsHint.className = "quiz-wizard__muted";
        settingsHint.textContent = t("quiz.wizard.generation_settings_hint");
        settingsBlock.appendChild(settingsHint);

        const qpaInput = appendLabeledNumberInput(
          settingsBlock,
          t("quiz.wizard.generation_questions_per_attempt_label"),
          {
            id: "quiz-gen-qpa",
            value: genSettingsInitial.questionsPerAttempt,
            min: MIN_QUESTIONS_PER_ATTEMPT,
            max: MAX_QUESTIONS_PER_ATTEMPT,
          },
        );

        const poolInput = appendLabeledNumberInput(
          settingsBlock,
          t("quiz.wizard.generation_pool_count_label"),
          {
            id: "quiz-gen-pool-count",
            value: genSettingsInitial.poolGenerationCount,
            min: 1,
            max: MAX_POOL_GENERATION_COUNT,
          },
        );

        const poolHint = document.createElement("p");
        poolHint.className = "quiz-wizard__recipients-hint";
        poolHint.textContent = t("quiz.wizard.generation_pool_count_hint");
        settingsBlock.appendChild(poolHint);

        const shapeHeading = document.createElement("p");
        shapeHeading.className = "quiz-wizard__subhead";
        shapeHeading.textContent = t("quiz.wizard.generation_shape_heading");
        settingsBlock.appendChild(shapeHeading);

        const optionInput = appendLabeledNumberInput(
          settingsBlock,
          t("quiz.wizard.generation_option_count_label"),
          {
            id: "quiz-gen-option-count",
            value: genSettingsInitial.optionCount,
            min: MIN_OPTION_COUNT,
            max: MAX_OPTION_COUNT,
          },
        );

        const minCorrectInput = appendLabeledNumberInput(
          settingsBlock,
          t("quiz.wizard.generation_min_correct_label"),
          {
            id: "quiz-gen-min-correct",
            value: genSettingsInitial.minCorrectOptions,
            min: 1,
            max: genSettingsInitial.optionCount,
          },
        );

        const maxCorrectInput = appendLabeledNumberInput(
          settingsBlock,
          t("quiz.wizard.generation_max_correct_label"),
          {
            id: "quiz-gen-max-correct",
            value: genSettingsInitial.maxCorrectOptions,
            min: 1,
            max: genSettingsInitial.optionCount,
          },
        );

        syncGenerationShapeInputs(
          genSettingsInitial.optionCount,
          minCorrectInput,
          maxCorrectInput,
        );

        if (genSettingsInitial.maxCorrectOptions <= 1) {
          const singleCorrectNote = document.createElement("p");
          singleCorrectNote.className = "quiz-wizard__recipients-hint";
          singleCorrectNote.textContent = t(
            "quiz.wizard.generation_single_correct_note",
          );
          settingsBlock.appendChild(singleCorrectNote);
        }

        qpaInput.addEventListener("input", () => {
          if (!poolCountTouched) {
            const qpa = Math.max(
              MIN_QUESTIONS_PER_ATTEMPT,
              Math.min(
                MAX_QUESTIONS_PER_ATTEMPT,
                parseInt(qpaInput.value, 10) ||
                  genSettingsInitial.questionsPerAttempt,
              ),
            );
            poolInput.value = String(defaultPoolGenerationCount(qpa));
          }
        });

        poolInput.addEventListener("input", () => {
          poolCountTouched = true;
        });

        optionInput.addEventListener("input", () => {
          const oc = Math.max(
            MIN_OPTION_COUNT,
            Math.min(
              MAX_OPTION_COUNT,
              parseInt(optionInput.value, 10) || genSettingsInitial.optionCount,
            ),
          );
          syncGenerationShapeInputs(oc, minCorrectInput, maxCorrectInput);
        });

        panel.appendChild(settingsBlock);

        const genActionsWrap = document.createElement("div");
        genActionsWrap.className = "quiz-wizard__generation-actions";
        const genRow = document.createElement("div");
        genRow.className = "quiz-wizard__generation-actions-row";

        const genStrip = document.createElement("div");
        genStrip.className = "quiz-wizard__buttons";

        const backMat = document.createElement("button");
        backMat.type = "button";
        backMat.className = "quiz-wizard__secondary";
        backMat.textContent = t("quiz.wizard.back_step");
        backMat.addEventListener("click", () => {
          currentStep = 2;
          paintStep();
        });
        genStrip.appendChild(backMat);

        const queueBtn = document.createElement("button");
        queueBtn.type = "button";
        queueBtn.className = "quiz-wizard__primary";
        queueBtn.textContent = t("quiz.wizard.generation_queue_button");
        queueBtn.disabled = pipe === "running";
        queueBtn.addEventListener("click", () => {
          void (async () => {
            queueBtn.disabled = true;
            const settingsValues = readGenerationSettingsFromInputs(
              qpaInput,
              poolInput,
              optionInput,
              minCorrectInput,
              maxCorrectInput,
              genSettingsInitial,
            );
            const shapeError = validateGenerationSettings(settingsValues);
            if (shapeError !== null) {
              status.textContent = t(shapeError);
              queueBtn.disabled = false;
              return;
            }
            const parsed = {
              configuration: configurationFromSettings(settingsValues),
            };
            // Synchronous POST keeps the server in running; repaint so a prior failed banner is not shown during the wait.
            if (detailCache !== null) {
              detailCache.generation_pipeline_status = "running";
            }
            paintStep();
            status.textContent = t("quiz.wizard.generation_busy");
            try {
              const res = await postQuizGeneration(qid, parsed, {
                signal: options.signal,
              });
              let body: unknown = null;
              if (
                res.status !== 403 &&
                res.status !== 401 &&
                res.status !== 404
              ) {
                try {
                  body = (await res.json()) as unknown;
                } catch {
                  body = null;
                }
              }
              if (options.signal.aborted) {
                queueBtn.disabled = false;
                return;
              }
              if (res.status === 502 || res.status === 503) {
                status.textContent = t("quiz.wizard.generation_failed");
                await reloadDetail(qid);
                paintStep();
                return;
              }
              if (res.status === 200 || res.status === 202) {
                const gps =
                  typeof body === "object" &&
                  body !== null &&
                  typeof (body as { generation_pipeline_status?: unknown })
                    .generation_pipeline_status === "string"
                    ? (body as { generation_pipeline_status: string })
                        .generation_pipeline_status
                    : "";
                if (gps === "failed") {
                  status.textContent = t(
                    "quiz.wizard.generation_completed_failed",
                  );
                } else {
                  status.textContent = t("quiz.wizard.generation_done_reload");
                }
                await reloadDetail(qid);
                paintStep();
                return;
              }
              if (res.status === 409) {
                let err = "";
                if (
                  typeof body === "object" &&
                  body !== null &&
                  typeof (body as { error?: unknown }).error === "string"
                ) {
                  err = (body as { error: string }).error;
                }
                if (err === "material_not_ready") {
                  status.textContent = t(
                    "quiz.wizard.generation_material_not_ready",
                  );
                } else if (err === "quiz_locked") {
                  status.textContent = t("quiz.wizard.generation_locked");
                } else if (err === "generation_in_progress") {
                  status.textContent = t(
                    "quiz.wizard.generation_in_progress_blocked",
                  );
                } else {
                  status.textContent = t("quiz.wizard.generation_failed");
                }
                await reloadDetail(qid);
                paintStep();
                return;
              }
              status.textContent = t("quiz.wizard.generation_failed");
              await reloadDetail(qid);
              paintStep();
            } catch (err: unknown) {
              if (err instanceof DOMException && err.name === "AbortError") {
                return;
              }
              status.textContent = t("quiz.wizard.generation_failed");
              await reloadDetail(qid);
              paintStep();
            } finally {
              queueBtn.disabled = false;
            }
          })();
        });
        genStrip.appendChild(queueBtn);

        const resetGenBtn = document.createElement("button");
        resetGenBtn.type = "button";
        resetGenBtn.className = "quiz-wizard__secondary";
        resetGenBtn.textContent = t("quiz.wizard.generation_reset_stuck_button");
        resetGenBtn.hidden = pipe !== "running";
        resetGenBtn.title = t("quiz.wizard.generation_reset_hint");
        resetGenBtn.addEventListener("click", () => {
          void (async () => {
            resetGenBtn.disabled = true;
            status.textContent = t("quiz.wizard.generation_reset_busy");
            try {
              const res = await postQuizGenerationPipelineReset(qid, {
                signal: options.signal,
              });
              if (options.signal.aborted) {
                resetGenBtn.disabled = false;
                return;
              }
              if (!res.ok) {
                if (res.status === 409) {
                  status.textContent = t(
                    "quiz.wizard.generation_reset_too_soon",
                  );
                } else {
                  status.textContent = t("quiz.wizard.generation_reset_failed");
                }
                resetGenBtn.disabled = false;
                return;
              }
              await reloadDetail(qid);
              paintStep();
            } catch (err: unknown) {
              if (err instanceof DOMException && err.name === "AbortError") {
                return;
              }
              status.textContent = t("quiz.wizard.generation_reset_failed");
            } finally {
              resetGenBtn.disabled = false;
            }
          })();
        });
        genStrip.appendChild(resetGenBtn);
        genRow.appendChild(genStrip);

        const genStatusCol = document.createElement("div");
        genStatusCol.className = "quiz-wizard__generation-status";

        const pipeLine = document.createElement("p");
        pipeLine.className =
          "quiz-wizard__muted quiz-wizard__generation-status-line";
        pipeLine.textContent = t("quiz.wizard.generation_pipeline_status", {
          status: pipe,
        });
        genStatusCol.appendChild(pipeLine);

        if (pipe === "failed") {
          const failBanner = document.createElement("p");
          failBanner.className =
            "quiz-wizard__highlight quiz-wizard__generation-status-line";
          failBanner.textContent = t("quiz.wizard.generation_failed_banner");
          genStatusCol.appendChild(failBanner);
        }
        if (pipe === "running") {
          const runBanner = document.createElement("p");
          runBanner.className =
            "quiz-wizard__muted quiz-wizard__generation-status-line";
          runBanner.textContent = t("quiz.wizard.generation_running_notice");
          genStatusCol.appendChild(runBanner);
          const resetHint = document.createElement("p");
          resetHint.className =
            "quiz-wizard__recipients-hint quiz-wizard__generation-status-line";
          resetHint.textContent = t("quiz.wizard.generation_reset_hint");
          genStatusCol.appendChild(resetHint);
        }

        status.remove();
        genStatusCol.appendChild(status);

        genRow.appendChild(genStatusCol);
        genActionsWrap.appendChild(genRow);
        panel.appendChild(genActionsWrap);

        const questions = detailCache.questions ?? [];
        const qpa = readQuestionsPerAttempt(detailCache.configuration);
        const optionCount = readOptionCount(detailCache.configuration);
        const poolShape = settingsFromConfiguration(
          detailCache.configuration ?? {},
        );
        const poolAllowsMulti = poolShape.maxCorrectOptions > 1;
        const activeCount = questions.filter((q) => q.active).length;
        const pipeRunning = pipe === "running";

        {
          const poolWrap = document.createElement("section");
          poolWrap.className = "quiz-wizard__pool";
          const poolHeadingRow = document.createElement("div");
          poolHeadingRow.className = "quiz-wizard__pool-heading";
          const poolH = document.createElement("h3");
          poolH.textContent = t("quiz.wizard.pool_section_title");
          poolHeadingRow.appendChild(poolH);
          const poolAddIcon = createIconButton(
            t("quiz.wizard.pool_add_aria"),
            "+",
            { disabled: pipeRunning },
          );
          poolHeadingRow.appendChild(poolAddIcon);
          poolWrap.appendChild(poolHeadingRow);
          const poolHelp = document.createElement("p");
          poolHelp.className = "quiz-wizard__muted";
          poolHelp.textContent = t("quiz.wizard.pool_section_help", {
            n: optionCount,
          });
          poolWrap.appendChild(poolHelp);

          if (pipeRunning) {
            const rl = document.createElement("p");
            rl.className = "quiz-wizard__muted";
            rl.textContent = t("quiz.wizard.pool_readonly_while_running");
            poolWrap.appendChild(rl);
          }

          if (activeCount < qpa) {
            const hint = document.createElement("p");
            hint.className = "quiz-wizard__muted";
            hint.textContent = t("quiz.wizard.pool_publish_hint", {
              active: activeCount,
              needed: qpa,
            });
            poolWrap.appendChild(hint);
          }

          if (questions.length === 0) {
            const empty = document.createElement("p");
            empty.className = "quiz-wizard__muted";
            empty.textContent = t("quiz.wizard.pool_empty");
            poolWrap.appendChild(empty);
          } else {
            const list = document.createElement("ul");
            list.className = "quiz-wizard__pool-list";
            for (const q of questions) {
              const li = document.createElement("li");
              li.className = "quiz-wizard__pool-item";

              const viewBlock = document.createElement("div");
              viewBlock.className = "quiz-wizard__pool-view";

              const stem = document.createElement("p");
              stem.className = "quiz-wizard__pool-stem";
              stem.textContent = q.stem;
              viewBlock.appendChild(stem);

              const choicesOl = document.createElement("ol");
              choicesOl.className = "quiz-wizard__pool-choices-view";
              choicesOl.setAttribute(
                "aria-label",
                t("quiz.wizard.pool_choices_aria"),
              );
              const cisV = correctIndicesForQuizRow(q);
              for (let ci = 0; ci < optionCount; ci++) {
                const choiceLi = document.createElement("li");
                choiceLi.appendChild(
                  document.createTextNode(quizChoiceTextAt(q, ci)),
                );
                if (cisV.includes(ci)) {
                  choiceLi.classList.add(
                    "quiz-wizard__pool-choice-view--correct",
                  );
                  const mark = document.createElement("span");
                  mark.className = "quiz-wizard__pool-choice-correct-mark";
                  mark.textContent = ` ${t("quiz.wizard.pool_correct_option_mark")}`;
                  choiceLi.appendChild(mark);
                }
                choicesOl.appendChild(choiceLi);
              }
              viewBlock.appendChild(choicesOl);

              if (!q.active) {
                const inactiveNote = document.createElement("p");
                inactiveNote.className = "quiz-wizard__muted";
                inactiveNote.textContent = t(
                  "quiz.wizard.pool_inactive_marker",
                );
                viewBlock.appendChild(inactiveNote);
              }

              const rowBtns = document.createElement("div");
              rowBtns.className =
                "quiz-wizard__buttons quiz-wizard__pool-item-actions";

              const editBtn = document.createElement("button");
              editBtn.type = "button";
              editBtn.className = "quiz-wizard__secondary";
              editBtn.textContent = t("quiz.wizard.pool_edit");
              editBtn.disabled = pipeRunning;
              rowBtns.appendChild(editBtn);

              const del = document.createElement("button");
              del.type = "button";
              del.className = "quiz-wizard__secondary";
              del.textContent = t("quiz.wizard.pool_delete");
              del.disabled = pipeRunning;

              rowBtns.appendChild(del);

              viewBlock.appendChild(rowBtns);
              li.appendChild(viewBlock);

              const editBlock = document.createElement("div");
              editBlock.className = "quiz-wizard__pool-edit";
              editBlock.style.display = "none";

              const stemEd = document.createElement("textarea");
              stemEd.rows = 3;
              stemEd.className = "quiz-wizard__recipients-input";
              stemEd.value = q.stem;
              editBlock.appendChild(stemEd);

              const edChoices: HTMLInputElement[] = [];
              const edWrap = document.createElement("div");
              edWrap.className = "quiz-wizard__pool-choices-edit";
              for (let ci = 0; ci < optionCount; ci++) {
                const lab = document.createElement("label");
                lab.className = "quiz-wizard__full-label";
                lab.textContent = t("quiz.wizard.pool_choice_label", {
                  n: ci + 1,
                });
                const inp = document.createElement("input");
                inp.type = "text";
                inp.className = "quiz-wizard__text-input";
                inp.value = quizChoiceTextAt(q, ci);
                edChoices.push(inp);
                lab.appendChild(inp);
                edWrap.appendChild(lab);
              }
              editBlock.appendChild(edWrap);

              const corWrap = document.createElement("div");
              corWrap.className = "quiz-wizard__pool-correct-edit";
              const corTitle = document.createElement("div");
              corTitle.className = "quiz-wizard__full-label";
              corTitle.textContent = t("quiz.wizard.pool_correct_label");
              corWrap.appendChild(corTitle);

              const corChecks: HTMLInputElement[] = [];
              let corSel: HTMLSelectElement | undefined;
              const presetCo = correctIndicesForQuizRow(q);
              if (poolAllowsMulti) {
                const hint = document.createElement("p");
                hint.className = "quiz-wizard__muted";
                hint.textContent = t("quiz.wizard.pool_correct_multi_hint");
                corWrap.appendChild(hint);
                for (let ci = 0; ci < optionCount; ci++) {
                  const row = document.createElement("label");
                  row.className = "quiz-wizard__full-label";
                  const cb = document.createElement("input");
                  cb.type = "checkbox";
                  cb.checked = presetCo.includes(ci);
                  corChecks.push(cb);
                  row.appendChild(cb);
                  row.appendChild(
                    document.createTextNode(
                      ` ${t("quiz.wizard.pool_choice_label", { n: ci + 1 })}`,
                    ),
                  );
                  corWrap.appendChild(row);
                }
              } else {
                corSel = document.createElement("select");
                for (let ci = 0; ci < optionCount; ci++) {
                  const opt = document.createElement("option");
                  opt.value = String(ci);
                  opt.textContent = String(ci + 1);
                  if (presetCo.includes(ci)) {
                    opt.selected = true;
                  }
                  corSel.appendChild(opt);
                }
                corWrap.appendChild(corSel);
              }
              editBlock.appendChild(corWrap);

              const edActions = document.createElement("div");
              edActions.className = "quiz-wizard__buttons";
              const saveEd = document.createElement("button");
              saveEd.type = "button";
              saveEd.className = "quiz-wizard__primary";
              saveEd.textContent = t("quiz.wizard.pool_save_edit");
              const cancelEd = document.createElement("button");
              cancelEd.type = "button";
              cancelEd.className = "quiz-wizard__secondary";
              cancelEd.textContent = t("quiz.wizard.pool_cancel_edit");
              edActions.appendChild(saveEd);
              edActions.appendChild(cancelEd);
              editBlock.appendChild(edActions);
              li.appendChild(editBlock);

              const showView = (): void => {
                viewBlock.style.display = "";
                editBlock.style.display = "none";
              };
              const showEdit = (): void => {
                viewBlock.style.display = "none";
                editBlock.style.display = "";
              };

              editBtn.addEventListener("click", () => {
                showEdit();
              });
              cancelEd.addEventListener("click", () => {
                stemEd.value = q.stem;
                for (let ri = 0; ri < edChoices.length; ri++) {
                  edChoices[ri].value = quizChoiceTextAt(q, ri);
                }
                if (poolAllowsMulti) {
                  const pr = correctIndicesForQuizRow(q);
                  for (let ri = 0; ri < corChecks.length; ri++) {
                    corChecks[ri].checked = pr.includes(ri);
                  }
                } else if (corSel) {
                  const prc = correctIndicesForQuizRow(q);
                  corSel.value = String(prc[0] ?? 0);
                }
                showView();
              });
              saveEd.addEventListener("click", () => {
                void (async () => {
                  saveEd.disabled = true;
                  status.textContent = t("quiz.wizard.pool_busy");
                  try {
                    const chVals = edChoices.map((el) => el.value.trim());
                    let correctPayload: number[];
                    if (poolAllowsMulti) {
                      correctPayload = [];
                      for (let ri = 0; ri < corChecks.length; ri++) {
                        if (corChecks[ri].checked) {
                          correctPayload.push(ri);
                        }
                      }
                      correctPayload.sort((a, b) => a - b);
                      if (
                        correctPayload.length < poolShape.minCorrectOptions ||
                        correctPayload.length > poolShape.maxCorrectOptions
                      ) {
                        status.textContent = t("quiz.wizard.pool_validation");
                        saveEd.disabled = false;
                        return;
                      }
                    } else {
                      correctPayload = [parseInt(corSel!.value, 10)];
                    }
                    const stemV = stemEd.value.trim();
                    if (!stemV || chVals.some((c) => !c)) {
                      status.textContent = t("quiz.wizard.pool_validation");
                      saveEd.disabled = false;
                      return;
                    }
                    const correctSet = new Set(correctPayload);
                    const choicesPayload = chVals.map((text, i) => ({
                      text,
                      correct: correctSet.has(i),
                    }));
                    const res = await patchQuizPool(
                      qid,
                      {
                        edit: [
                          {
                            question_id: q.question_id,
                            stem: stemV,
                            choices: choicesPayload,
                            active: q.active,
                          },
                        ],
                      },
                      { signal: options.signal },
                    );
                    if (res.status === 200) {
                      await reloadDetail(qid);
                      status.textContent = "";
                      paintStep();
                      return;
                    }
                    status.textContent = t("quiz.wizard.pool_failed");
                  } catch (err: unknown) {
                    if (
                      err instanceof DOMException &&
                      err.name === "AbortError"
                    ) {
                      return;
                    }
                    status.textContent = t("quiz.wizard.pool_failed");
                  } finally {
                    saveEd.disabled = false;
                  }
                })();
              });

              del.addEventListener("click", () => {
                void (async () => {
                  del.disabled = true;
                  status.textContent = t("quiz.wizard.pool_busy");
                  try {
                    const res = await patchQuizPool(
                      qid,
                      { delete: [q.question_id] },
                      { signal: options.signal },
                    );
                    if (res.status === 200) {
                      await reloadDetail(qid);
                      status.textContent = "";
                      paintStep();
                      return;
                    }
                    status.textContent = t("quiz.wizard.pool_failed");
                  } catch (err: unknown) {
                    if (
                      err instanceof DOMException &&
                      err.name === "AbortError"
                    ) {
                      return;
                    }
                    status.textContent = t("quiz.wizard.pool_failed");
                  } finally {
                    del.disabled = false;
                  }
                })();
              });

              list.appendChild(li);
            }
            poolWrap.appendChild(list);
          }

          const addBlock = document.createElement("div");
          addBlock.className = "quiz-wizard__pool-add";
          if (questions.length === 0) {
            addBlock.classList.add("quiz-wizard__pool-add--visible");
          }

          poolAddIcon.addEventListener("click", () => {
            addBlock.classList.add("quiz-wizard__pool-add--visible");
            addStem.focus();
          });

          const addStem = document.createElement("textarea");
          addStem.rows = 2;
          addStem.className = "quiz-wizard__recipients-input";
          addStem.placeholder = t("quiz.wizard.pool_stem_placeholder");
          addStem.disabled = pipeRunning;
          addBlock.appendChild(addStem);

          const choiceInputs: HTMLInputElement[] = [];
          const choiceCol = document.createElement("div");
          choiceCol.className = "quiz-wizard__pool-choices";
          for (let i = 0; i < optionCount; i++) {
            const lab = document.createElement("label");
            lab.className = "quiz-wizard__full-label";
            lab.textContent = t("quiz.wizard.pool_choice_label", { n: i + 1 });
            const inp = document.createElement("input");
            inp.type = "text";
            inp.className = "quiz-wizard__text-input";
            inp.disabled = pipeRunning;
            choiceInputs.push(inp);
            lab.appendChild(inp);
            choiceCol.appendChild(lab);
          }
          addBlock.appendChild(choiceCol);

          const addCorWrap = document.createElement("div");
          addCorWrap.className = "quiz-wizard__pool-correct-edit";
          const addCorTitle = document.createElement("div");
          addCorTitle.className = "quiz-wizard__full-label";
          addCorTitle.textContent = t("quiz.wizard.pool_correct_label");
          addCorWrap.appendChild(addCorTitle);
          const addCorChecks: HTMLInputElement[] = [];
          let addCorSel: HTMLSelectElement | undefined;
          if (poolAllowsMulti) {
            const hintA = document.createElement("p");
            hintA.className = "quiz-wizard__muted";
            hintA.textContent = t("quiz.wizard.pool_correct_multi_hint");
            addCorWrap.appendChild(hintA);
            for (let i = 0; i < optionCount; i++) {
              const row = document.createElement("label");
              row.className = "quiz-wizard__full-label";
              const cb = document.createElement("input");
              cb.type = "checkbox";
              cb.disabled = pipeRunning;
              addCorChecks.push(cb);
              row.appendChild(cb);
              row.appendChild(
                document.createTextNode(
                  ` ${t("quiz.wizard.pool_choice_label", { n: i + 1 })}`,
                ),
              );
              addCorWrap.appendChild(row);
            }
          } else {
            addCorSel = document.createElement("select");
            for (let i = 0; i < optionCount; i++) {
              const opt = document.createElement("option");
              opt.value = String(i);
              opt.textContent = String(i + 1);
              addCorSel.appendChild(opt);
            }
            addCorSel.disabled = pipeRunning;
            addCorWrap.appendChild(addCorSel);
          }
          addBlock.appendChild(addCorWrap);

          const addBtn = document.createElement("button");
          addBtn.type = "button";
          addBtn.className = "quiz-wizard__secondary";
          addBtn.textContent = t("quiz.wizard.pool_add_button");
          addBtn.disabled = pipeRunning;
          addBtn.addEventListener("click", () => {
            void (async () => {
              const stemV = addStem.value.trim();
              const choices = choiceInputs.map((el) => el.value.trim());
              let addCorrect: number[];
              if (poolAllowsMulti) {
                addCorrect = [];
                for (let ri = 0; ri < addCorChecks.length; ri++) {
                  if (addCorChecks[ri].checked) {
                    addCorrect.push(ri);
                  }
                }
                addCorrect.sort((a, b) => a - b);
                if (
                  addCorrect.length < poolShape.minCorrectOptions ||
                  addCorrect.length > poolShape.maxCorrectOptions
                ) {
                  status.textContent = t("quiz.wizard.pool_validation");
                  return;
                }
              } else {
                addCorrect = [parseInt(addCorSel!.value, 10)];
              }
              if (!stemV || choices.some((c) => !c)) {
                status.textContent = t("quiz.wizard.pool_validation");
                return;
              }
              addBtn.disabled = true;
              status.textContent = t("quiz.wizard.pool_busy");
              try {
                const addCorrectSet = new Set(addCorrect);
                const choicesOut = choices.map((text, i) => ({
                  text,
                  correct: addCorrectSet.has(i),
                }));
                const res = await patchQuizPool(
                  qid,
                  {
                    add: [
                      {
                        stem: stemV,
                        choices: choicesOut,
                        active: true,
                      },
                    ],
                  },
                  { signal: options.signal },
                );
                if (res.status === 200) {
                  addStem.value = "";
                  for (const el of choiceInputs) {
                    el.value = "";
                  }
                  for (const c of addCorChecks) {
                    c.checked = false;
                  }
                  await reloadDetail(qid);
                  status.textContent = "";
                  paintStep();
                  return;
                }
                if (res.status === 409) {
                  status.textContent = t("quiz.wizard.generation_locked");
                } else {
                  status.textContent = t("quiz.wizard.pool_failed");
                }
              } catch (err: unknown) {
                if (err instanceof DOMException && err.name === "AbortError") {
                  return;
                }
                status.textContent = t("quiz.wizard.pool_failed");
              } finally {
                addBtn.disabled = pipeRunning;
              }
            })();
          });
          addBlock.appendChild(addBtn);
          poolWrap.appendChild(addBlock);
          panel.appendChild(poolWrap);
        }

        if (pipeRunning) {
          step3PollTimer = window.setInterval(() => {
            void (async () => {
              if (!(await reloadDetail(qid))) {
                return;
              }
              paintStep();
            })();
          }, 2100);
        }

        const continueStrip = document.createElement("div");
        continueStrip.className = "quiz-wizard__buttons";
        const nextGen = document.createElement("button");
        nextGen.type = "button";
        nextGen.className = "quiz-wizard__primary";
        nextGen.textContent = t("quiz.wizard.continue");
        const poolReady = activeCount >= qpa;
        nextGen.disabled = pipeRunning || !poolReady;
        nextGen.addEventListener("click", () => {
          if (pipeRunning) {
            status.textContent = t("quiz.wizard.generation_busy");
            return;
          }
          if (!poolReady) {
            status.textContent = t(
              "quiz.wizard.generation_continue_blocked_pool",
            );
            return;
          }
          currentStep = 4;
          paintStep();
        });
        continueStrip.appendChild(nextGen);
        panel.appendChild(continueStrip);
        return;

      case 4:
        dockStatusBeforePanel();
        setProgress(4);
        panel.replaceChildren();
        const s4Heading = document.createElement("h2");
        s4Heading.textContent = t("quiz.wizard.step_invite_heading");
        panel.appendChild(s4Heading);
        const s4Lead = document.createElement("p");
        s4Lead.textContent = t("quiz.wizard.step_invite_body");
        panel.appendChild(s4Lead);

        const step4Strip = document.createElement("div");
        step4Strip.className = "quiz-wizard__buttons";
        const backToGeneration = document.createElement("button");
        backToGeneration.type = "button";
        backToGeneration.className = "quiz-wizard__secondary";
        backToGeneration.textContent = t("quiz.wizard.back_step");
        backToGeneration.addEventListener("click", () => {
          currentStep = 3;
          paintStep();
        });
        step4Strip.appendChild(backToGeneration);
        panel.appendChild(step4Strip);

        const recipLabel = document.createElement("label");
        recipLabel.className = "quiz-wizard__full-label";
        recipLabel.htmlFor = "quiz-wizard-recipients";
        recipLabel.textContent = t("quiz.wizard.recipients_label");
        panel.appendChild(recipLabel);

        const ta = document.createElement("textarea");
        ta.id = "quiz-wizard-recipients";
        ta.setAttribute("aria-describedby", "quiz-wizard-recipients-hint");
        ta.rows = 6;
        ta.className = "quiz-wizard__recipients-input";
        ta.placeholder = t("quiz.wizard.recipients_placeholder");
        panel.appendChild(ta);

        const helper = document.createElement("p");
        helper.id = "quiz-wizard-recipients-hint";
        helper.className = "quiz-wizard__recipients-hint";
        helper.textContent = t("quiz.wizard.recipients_hint");
        panel.appendChild(helper);

        const sendBtn = document.createElement("button");
        sendBtn.type = "button";
        sendBtn.className = "quiz-wizard__primary";
        sendBtn.textContent = t("quiz.wizard.publish_button");

        panel.appendChild(sendBtn);

        sendBtn.addEventListener("click", () => {
          void (async () => {
            sendBtn.disabled = true;
            const lines = ta.value.split("\n");
            const emails = lines
              .map((ln) => ln.trim())
              .filter((ln) => ln.length > 0);

            status.textContent = "";
            try {
              const pubRes = await postPublishQuizRecipients(qid, emails, {
                signal: options.signal,
              });

              if (options.signal.aborted) {
                return;
              }

              if (pubRes.status === 400) {
                let errCode = "";
                try {
                  const b = (await pubRes.json()) as { error?: string };
                  errCode = typeof b.error === "string" ? b.error : "";
                } catch {
                  errCode = "";
                }
                if (errCode === "pool_too_small") {
                  status.textContent = t("quiz.wizard.publish_pool_too_small");
                } else {
                  status.textContent = t("quiz.wizard.publish_validation");
                }
                sendBtn.disabled = false;
                return;
              }

              if (pubRes.status === 409) {
                status.textContent = t("quiz.wizard.publish_conflict");
                sendBtn.disabled = false;
                return;
              }

              if (!pubRes.ok) {
                status.textContent = t("quiz.wizard.publish_failed");
                sendBtn.disabled = false;
                return;
              }

              let payload: unknown;
              try {
                payload = (await pubRes.json()) as unknown;
              } catch {
                status.textContent = t("quiz.wizard.publish_failed");
                sendBtn.disabled = false;
                return;
              }

              if (!isPublishPayload(payload)) {
                status.textContent = t("quiz.wizard.publish_failed");
                sendBtn.disabled = false;
                return;
              }

              lastPublishRows = payload.invitations;
              detailCache =
                detailCache !== null
                  ? { ...detailCache, status: "published" }
                  : detailCache;

              await reloadDetail(qid);

              paintStep();
            } catch (err: unknown) {
              if (err instanceof DOMException && err.name === "AbortError") {
                return;
              }
              status.textContent = t("quiz.wizard.publish_failed");
            } finally {
              sendBtn.disabled = false;
            }
          })();
        });
        return;
    }
  };

  /**
   * @param panel Wizard step container.
   * @param advance Called when advancing to the following step (**no-op** persistence).
   */
  function appendNavigation(panel: HTMLElement, advance: () => void): void {
    const strip = document.createElement("div");
    strip.className = "quiz-wizard__buttons";

    const backStep = document.createElement("button");
    backStep.type = "button";
    backStep.className = "quiz-wizard__secondary";
    backStep.textContent = t("quiz.wizard.back_step");
    backStep.hidden = currentStep <= 1;
    strip.appendChild(backStep);

    backStep.addEventListener("click", () => {
      currentStep = Math.max(1, currentStep - 1);
      paintStep();
    });

    const next = document.createElement("button");
    next.type = "button";
    next.className = "quiz-wizard__primary";
    next.textContent = t("quiz.wizard.continue");
    strip.appendChild(next);

    next.addEventListener("click", () => {
      advance();
    });

    panel.appendChild(strip);
  }

  try {
    if (quizIdFromUrl !== undefined && !(await reloadDetail(quizIdFromUrl))) {
      paintStep();
      return;
    }
    paintStep();
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return;
    }
    status.textContent = t("quiz.wizard.quiz_missing");
  }
}
